import { historyService } from '@/features/design/history/api/history.service';
import { fetchImageBlob } from '@/shared/lib/utils/cors-image-loader';
import {
  isRenderHistoryItem,
  type RenderHistoryItem,
} from '@/features/design/history/model/history.types';
import type { RenderPickerItem } from '../model/types';
import { RENDER_PICKER_PAGE_SIZE } from '../model/types';

/**
 * Transform RenderHistoryItem to RenderPickerItem
 */
function transformToPickerItem(item: RenderHistoryItem): RenderPickerItem {
  return {
    id: item.id,
    imageUrl: item.attributes.image_url || '',
    conceptName: item.relationships.concept.meta.name || null,
    projectName: item.relationships.project.meta.name,
    isFavorite: false, // Renders don't have is_favorite, it's on concept level
    isUpscaled: item.attributes.is_upscaled,
    createdAt: item.attributes.created_at,
    conceptId: item.relationships.concept.data?.id ?? '',
    projectId: item.relationships.project.data?.id ?? '',
  };
}

export interface GetRendersParams {
  page: number;
  favoritesOnly?: boolean;
}

export interface GetRendersResult {
  items: RenderPickerItem[];
  hasMore: boolean;
  totalPages: number;
}

/**
 * Service for fetching renders for the picker
 */
export const renderPickerService = {
  /**
   * Fetch renders for the picker with pagination
   */
  async getRenders({
    page,
    favoritesOnly = false,
  }: GetRendersParams): Promise<GetRendersResult> {
    const response = await historyService.getJobsHistory({
      filter: {
        status: 'completed',
        ...(favoritesOnly && { is_favorite: true }),
      },
      page: {
        number: page,
        size: RENDER_PICKER_PAGE_SIZE,
      },
      sort: '-created_at', // Newest first
    });

    // Filter to only render items with images
    const renderItems = response.data
      .filter(isRenderHistoryItem)
      .filter((item) => item.attributes.image_url);

    const items = renderItems.map(transformToPickerItem);

    const lastPage = response.meta?.last_page ?? 1;
    const hasMore = page < lastPage;

    return {
      items,
      hasMore,
      totalPages: lastPage,
    };
  },

  /**
   * Download a render image and return it as a File
   */
  async downloadRender(imageUrl: string, filename?: string): Promise<File> {
    // The picker grid already showed this URL via <img> — fetch through the
    // cache-segregated CORS loader, or Chrome serves the poisoned cache entry.
    const blob = await fetchImageBlob(imageUrl, 'picker');
    const mimeType = blob.type || 'image/jpeg';
    const extension = mimeType.split('/')[1] || 'jpg';
    const finalFilename = filename || `render_${Date.now()}.${extension}`;

    return new File([blob], finalFilename, { type: mimeType });
  },
};
